import { BaseButtonSquare, TBaseButtonSquareProps } from 'src/components/BaseButtonSquare';
import { BaseIcon, TBaseIconProps } from 'src/components/BaseIcon';
import { DEFAULT_COMMUNITY_NAME, START_LOCATION } from 'src/constants/application';
import { EMenuColor, THeaderProps } from './Header.types';
import { EModalSize, EPredefinedModalIds } from 'src/constants/modals';
import { Link, useHistory } from 'react-router-dom';
import React, { useContext, useRef, useState } from 'react';
import { closeModal, openModal } from 'src/redux/modals/modals.slice';
import { routesConfig, routesTitles } from 'src/routes/routes.config';
import {
  selectActiveConfigurationJobUuid,
  selectActiveConfigurationUuid,
  selectAssetsTreeRelations,
  selectCommunityAsset,
  selectCommunityName,
  selectReadOnly,
  selectSimulationStatus,
} from 'src/redux/configuration/configuration.selectors';
import { selectAppMode, selectIsEmbed } from 'src/redux/application/application.selectors';
import {
  setModalAssetManagerActiveView,
  setModalCommunitySummaryView,
  setModalMapHeroDismissed,
  setOnBoardingStep,
  setGuideStep,
} from 'src/redux/application/application.slice';

import { BaseButton } from 'src/components/BaseButton';
import { BaseHamburger } from 'src/components/BaseHamburger';
import { BaseLink } from 'src/components/BaseLink';
import { BaseOverflowMenu } from 'src/components/BaseOverflowMenu';
import { BaseTooltipTrigger } from 'src/components/BaseTooltip';
import { BurguerButton } from 'src/components/LandingPage/BurguerButton';
import { EAppMode } from 'src/utils/appMode';
import { EDomIds } from 'src/constants/domSelectors';
import { EModalAssetsManagerView } from 'src/components/ModalAssetsManager';
import { ModalBurgerNavigation } from '../ModalBurgerNavigation';
import { NAV_LINKS } from './constants';
import { ShareSimulation } from 'src/components/ShareSimulation';
import { UserAvatar } from 'src/components/UserAvatar';
import WorldMapContext from 'src/contexts/WorldMapContext';
import classnames from 'classnames';
import s from './Header.module.scss';
import { selectIsLoggedIn } from 'src/redux/auth/auth.selectors';
import { selectIsModalOpened } from 'src/redux/modals/modals.selectors';
import { setSelectedAssetUuid } from 'src/redux/configuration/configuration.slice';
import { useAppDispatch } from 'src/redux/store';
import { useAppLocation } from 'src/hooks/useAppLocation';
import { useConfigurationFlowState } from 'src/hooks/useConfigurationFlowState';
import { useConfigurationUtils } from 'src/hooks/useConfigurationUtils';
import { useModal } from 'src/hooks/useModal';
import { useSelector } from 'react-redux';
import { useSimulationButtons } from 'src/hooks/useSimulationButtons';
import { selectTutorialStep } from 'src/redux/application/application.selectors';

export enum EHeaderMode {
  Default = 'DEFAULT',
  Community = 'COMMUNITY',
}

const selectColorMenuLandingPage = {
  [EMenuColor.AlternativeOne]: s.menuColorLandingPageAlt_1,
  [EMenuColor.AlternativeTwo]: s.menuColorLandingPageAlt_2,
};

const selectBgBlueLandingPage = {
  [EMenuColor.AlternativeOne]: s.blurAlt_1,
  [EMenuColor.AlternativeTwo]: s.blurAlt_2,
};

export const Header: React.FC<THeaderProps> = ({
  isLandingPage,
  headerColorMode,
  textColorLandingpage,
}) => {
  const location = useAppLocation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const isEmbed = useSelector(selectIsEmbed);
  const simulationStatus = useSelector(selectSimulationStatus);
  const communityAsset = useSelector(selectCommunityAsset);
  const activeConfigurationUuid = useSelector(selectActiveConfigurationUuid);
  const activeConfigurationJobUuid = useSelector(selectActiveConfigurationJobUuid);
  const assetsTreeRelations = useSelector(selectAssetsTreeRelations);
  const { discardCurrentConfiguration } = useConfigurationUtils();
  const { headerMode, communityIsReadyToSave } = useConfigurationFlowState();
  const [openButtonMenu, setOpenButtonMenu] = useState(false);
  const { runButtonOptions, runButtonState, stopButtonOptions } = useSimulationButtons({
    jobUuid: activeConfigurationJobUuid,
    configurationUuid: activeConfigurationUuid,
  });
  const { mapService } = useContext(WorldMapContext);
  const appMode = useSelector(selectAppMode);
  const readOnly = useSelector(selectReadOnly);

  const showAbortBtn = ['running', 'paused', 'queued', 'started', 'initializing'].includes(
    simulationStatus as string,
  );
  const communityName = useSelector(selectCommunityName);
  const isLoggedIn = useSelector(selectIsLoggedIn);

  const title =
    headerMode === EHeaderMode.Community ? communityName : routesTitles[location.routeName];

  const { id: burgerMenuModalId } = useModal();
  const { id: shareMenuModalId } = useModal();
  const isBurgerMenuOpened = useSelector(selectIsModalOpened(burgerMenuModalId));
  const isShareMenuOpened = useSelector(selectIsModalOpened(shareMenuModalId));
  const modalRefShare = useRef<HTMLButtonElement>(null);
  const tutorialStep = useSelector(selectTutorialStep);

  function handleHamburgerClick() {
    if (isBurgerMenuOpened) {
      dispatch(closeModal(burgerMenuModalId));
    } else {
      dispatch(openModal(burgerMenuModalId));
    }
  }

  const handleCreateCommunityClick = () => {
    dispatch(setModalAssetManagerActiveView(EModalAssetsManagerView.AddCommunity));
    dispatch(setOnBoardingStep(0));
    dispatch(setGuideStep(1));
    dispatch(openModal(EPredefinedModalIds.MODAL_ONBOARDING));
    discardCurrentConfiguration();
    dispatch(setModalMapHeroDismissed(true));
    history.push(routesConfig.singularityMapCreate());
    mapService?.fitBounds(START_LOCATION);
  };

  const handleCommunityDiscard = () => {
    dispatch(setModalAssetManagerActiveView(EModalAssetsManagerView.AddCommunity));
    dispatch(setOnBoardingStep(0));
    dispatch(setGuideStep(0));
    dispatch(closeModal(EPredefinedModalIds.MODAL_ONBOARDING));
    discardCurrentConfiguration({ zoomOut: true });
  };
  const closeShareSimulationModal = () => {
    dispatch(closeModal(shareMenuModalId));
  };

  const titleSideIcon:
    | { icon: TBaseIconProps['icon']; theme: TBaseButtonSquareProps['theme'] }
    | undefined = (() => {
    switch (location.routeName) {
      case 'singularityMap': {
        if (headerMode === EHeaderMode.Community) {
          return {
            icon: 'simulation' as const,
            theme: 'flat-blue' as const,
          };
        }

        return {
          icon: 'globe' as const,
          theme: 'flat-green' as const,
        };
      }

      default:
        return;
    }
  })();

  const titleSideButtons: JSX.Element | null = (() => {
    const defaultBtns = (
      <>
        <span className={s.vertLineB}></span>
        <button type="button" className="click-area">
          <BaseIcon icon="share-arrow" className={s.aloneIcon} />
        </button>
      </>
    );

    switch (location.routeName) {
      case 'singularityMap': {
        if (headerMode === EHeaderMode.Community) {
          const showSettingsButton =
            !!communityAsset && !!(assetsTreeRelations[communityAsset.uuid].length > 1);

          return (
            <>
              {showSettingsButton && (
                <>
                  <button
                    type="button"
                    className="click-area"
                    onClick={() => {
                      dispatch(setSelectedAssetUuid(communityAsset?.uuid));
                      if (communityName === DEFAULT_COMMUNITY_NAME) {
                        dispatch(setModalCommunitySummaryView(EModalAssetsManagerView.Form));
                      } else {
                        dispatch(setModalCommunitySummaryView(EModalAssetsManagerView.Summary));
                      }
                    }}>
                    <BaseIcon icon="settings" className={s.aloneIcon} />
                  </button>
                  <span className={s.vertLineB}></span>
                </>
              )}

              <button
                ref={modalRefShare}
                type="button"
                className="click-area"
                onClick={() => {
                  dispatch(
                    isShareMenuOpened ? closeModal(shareMenuModalId) : openModal(shareMenuModalId),
                  );
                }}>
                <BaseIcon
                  icon="share"
                  className={classnames(s.aloneIcon, isShareMenuOpened ? s.greenMenu : '')}
                />
              </button>

              {/* <span className={s.vertLineB}></span> */}

              {/* {shareBtn} */}

              {/* "Compare" won't be available in the 1st release */}
              {/* <span className={s.vertLineB}></span>

              <button type="button" className="click-area">
                <BaseIcon icon="compare" className={s.aloneIcon} />
              </button> */}
            </>
          );
        }
        return defaultBtns;
      }

      default:
        return defaultBtns;
    }
  })();

  const LoggedComponent = (
    <header id={EDomIds.HEADER} className={classnames(s.container, s.itemsCenter)}>
      <nav className={classnames(s.nav_con, s.itemsCenter)}>
        <BaseHamburger
          isOpened={isBurgerMenuOpened}
          onClick={handleHamburgerClick}
          className={s.hamburger}
        />
        <ModalBurgerNavigation modalId={burgerMenuModalId} />
        <BaseLink
          isNavLink={true}
          to={routesConfig.home()}
          className={s.logoLink}
          activeStyle={false}>
          <BaseIcon icon="gs-logo" />
        </BaseLink>

        <span className={s.vertLineA}></span>

        {titleSideIcon?.icon === 'simulation' && !showAbortBtn && (
          <BaseIcon
            icon="arrow-left-full"
            size={17}
            className={s.backIcon}
            onClick={handleCommunityDiscard}
          />
        )}

        {titleSideIcon && (
          <div className={s.titleSideIcon}>
            <BaseButtonSquare
              icon={titleSideIcon.icon}
              theme={titleSideIcon.theme}
              size="2"
              svgSize="3"
            />
          </div>
        )}

        <span className={s.title}>{title}</span>

        {titleSideButtons}
      </nav>

      <nav className={classnames(s.nav_con, s.mla, s.itemsCenter)}>
        {!isEmbed &&
          (headerMode === EHeaderMode.Community ? (
            <>
              <BaseButtonSquare
                theme="flat-gray"
                icon="close"
                size="3"
                svgSize="2"
                onClick={handleCommunityDiscard}
              />

              <span className={s.vertLineC}></span>

              {!stopButtonOptions.disabled && (
                <button
                  type="button"
                  className={classnames(s.abortBtn, s.centerAll)}
                  onClick={() => stopButtonOptions.onClick && stopButtonOptions.onClick()}
                  disabled={stopButtonOptions.disabled}>
                  <BaseIcon icon={stopButtonOptions.icon} size={19.5} />
                </button>
              )}
              {activeConfigurationUuid ? (
                !readOnly ? (
                  <BaseButton
                    className={s.runPauseBtn}
                    theme={runButtonState === 'pause' ? 'secondary' : undefined}
                    onClick={() => runButtonOptions.onClick && runButtonOptions.onClick()}
                    icon={runButtonOptions.icon}
                    disabled={runButtonOptions.disabled}>
                    {runButtonOptions.title}
                  </BaseButton>
                ) : null
              ) : communityIsReadyToSave ? (
                <BaseButton
                  type="submit"
                  form={EPredefinedModalIds.MODAL_COMMUNITY_ASSET_MANAGER_FORM}
                  className={s.buildCommunityBtn}
                  onClick={() => {
                    dispatch(setSelectedAssetUuid(communityAsset?.uuid));
                    dispatch(setModalCommunitySummaryView(EModalAssetsManagerView.Form));
                  }}>
                  Save Community
                </BaseButton>
              ) : null}
            </>
          ) : (
            <BaseButton
              className={classnames(s.buildCommunityBtn, s.ml25)}
              onClick={handleCreateCommunityClick}>
              Build Community
            </BaseButton>
          ))}
      </nav>
      <UserAvatar></UserAvatar>
      {/* User menu */}
      <BaseOverflowMenu
        relativeElement={modalRefShare}
        modalId={shareMenuModalId}
        position={{
          side: 'bottom',
          anchorPosition: 6,
        }}
        size={EModalSize.M280}
        className={s.shareMenu}>
        <ShareSimulation close={closeShareSimulationModal} />
      </BaseOverflowMenu>
    </header>
  );

  const NotLoggedComponent = (
    <header className={classnames(s.notLoggedContainer, s.itemsCenter)}>
      <nav className={classnames(s.nav_con, s.itemsCenter)}>
        <BaseLink
          isNavLink={true}
          to={routesConfig.home()}
          className={classnames(s.logoLink, s.logoLinkNotLogged)}
          activeStyle={false}>
          <BaseIcon icon="gs-logo" />
        </BaseLink>

        {NAV_LINKS.map((link) => {
          return (
            <BaseLink
              key={link.route}
              isNavLink={location?.routeName === link.route}
              to={routesConfig[link.route]()}
              className={classnames(s.pageNavLink, 'click-area')}>
              {link.title}
            </BaseLink>
          );
        })}
      </nav>

      <nav
        style={{ zIndex: tutorialStep === 1 ? 99999 : 100 }}
        className={classnames(s.nav_con, s.itemsCenter, s.mla)}>
        <button
          type="button"
          className={s.loginLink}
          onClick={() => dispatch(openModal(EPredefinedModalIds.MODAL_AUTH_LOGIN))}>
          Login
        </button>
        <span className={s.vertLineD}></span>
        <button
          type="button"
          className={s.registerLink}
          onClick={() => dispatch(openModal(EPredefinedModalIds.MODAL_AUTH_REGISTER))}>
          Register
        </button>
        <span className={s.vertLineD}></span>
        {!isEmbed &&
          (headerMode === EHeaderMode.Community ? (
            communityIsReadyToSave ? (
              <BaseButton
                type="submit"
                form={EPredefinedModalIds.MODAL_COMMUNITY_ASSET_MANAGER_FORM}
                className={classnames(s.buildCommunityBtn, s.ml25)}
                onClick={() => {
                  if (!isLoggedIn) {
                    dispatch(openModal(EPredefinedModalIds.MODAL_AUTH_REGISTER));
                  }
                  dispatch(setSelectedAssetUuid(communityAsset?.uuid));
                  if (communityName === DEFAULT_COMMUNITY_NAME) {
                    dispatch(setModalCommunitySummaryView(EModalAssetsManagerView.Form));
                  } else {
                    dispatch(setModalCommunitySummaryView(EModalAssetsManagerView.Summary));
                  }
                }}>
                {communityName === DEFAULT_COMMUNITY_NAME ? 'Save Community' : 'Run Simulation'}
              </BaseButton>
            ) : (
              <BaseButton
                className={s.cancelBtn}
                icon="close"
                theme="grey3"
                onClick={handleCommunityDiscard}>
                Cancel
              </BaseButton>
            )
          ) : (
            <BaseButton
              className={classnames(s.buildCommunityBtn, s.ml25)}
              onClick={handleCreateCommunityClick}>
              Build Community
            </BaseButton>
          ))}
      </nav>
    </header>
  );

  const headerColorModes = {
    white: s.white,
    dark: s.dark,
  };

  const LandingPageMenuItem = () => {
    return (
      <>
        {NAV_LINKS.map((link) => {
          return (
            <BaseLink
              key={link.route}
              isNavLink={location?.routeName === link.route}
              to={routesConfig[link.route]()}
              className={classnames(
                s.pageNavLink,
                'click-area',
                textColorLandingpage ? selectColorMenuLandingPage?.[textColorLandingpage] : '',
              )}
              theme="3">
              {link.title}
            </BaseLink>
          );
        })}
      </>
    );
  };

  const IconsItems = () => {
    return (
      <>
        <BaseLink
          theme="3"
          target="_blank"
          to="https://gridsingularity.github.io/gsy-e/documentation/">
          <BaseTooltipTrigger
            triggerClassName={''}
            tooltipClassName={s.helperTooltip}
            position="bottom"
            tooltipChildren={'Grid Singularity Documentation'}
            wordWrap={true}
            offset={20}>
            <BaseIcon className={s.icons} icon="wiki" size={22} />
          </BaseTooltipTrigger>
        </BaseLink>
        <BaseLink theme="3" target="_blank" to="https://gridsingularity.medium.com/">
          <BaseTooltipTrigger
            triggerClassName={''}
            tooltipClassName={s.helperTooltip}
            position="bottom"
            tooltipChildren={'Grid Singularity Medium Blog'}
            wordWrap={true}
            offset={20}>
            <BaseIcon className={s.icons} icon="medium" size={18} />
          </BaseTooltipTrigger>
        </BaseLink>
        <BaseLink theme="3" target="_blank" to="https://twitter.com/gridsingularity">
          <BaseTooltipTrigger
            triggerClassName={''}
            tooltipClassName={s.helperTooltip}
            position="bottom"
            tooltipChildren={'Grid Singularity Twitter Handle'}
            wordWrap={true}
            offset={20}>
            <BaseIcon className={s.icons} icon="twitter" size={18} />
          </BaseTooltipTrigger>
        </BaseLink>
        <BaseLink theme="3" target="_blank" to="https://gsycommunity.slack.com/">
          <BaseTooltipTrigger
            triggerClassName={''}
            tooltipClassName={s.helperTooltip}
            position="bottom"
            tooltipChildren={'Grid Singularity Slack Channel'}
            wordWrap={true}
            offset={20}>
            <BaseIcon className={s.icons} icon="slack" size={18} />
          </BaseTooltipTrigger>
        </BaseLink>
        <BaseLink theme="3" target="_blank" to="https://www.linkedin.com/company/grid-singularity">
          <BaseTooltipTrigger
            triggerClassName={''}
            tooltipClassName={s.helperTooltip}
            position="bottom"
            tooltipChildren={'Grid Singularity LinkedIn Profile'}
            wordWrap={true}
            offset={20}>
            <BaseIcon className={s.icons} icon="linkedin" size={18} />
          </BaseTooltipTrigger>
        </BaseLink>
        <BaseLink theme="3" to="mailto:contact@gridsingularity.com">
          <BaseTooltipTrigger
            triggerClassName={''}
            tooltipClassName={s.helperTooltip}
            position="bottom"
            tooltipChildren={'Grid Singularity Email'}
            wordWrap={true}
            offset={20}>
            <BaseIcon className={classnames(s.icons, s.mailIcon)} icon="email" size={22} />
          </BaseTooltipTrigger>
        </BaseLink>
      </>
    );
  };

  const selectLogoLandingPage = () => {
    if (textColorLandingpage === EMenuColor.AlternativeOne) {
      return <BaseIcon icon="landing-logo-white" />;
    }

    if (headerColorMode === 'dark') return <BaseIcon icon="landing-logo-white" />;
    if (headerColorMode === 'white') return <BaseIcon icon="landing-logo-dark" />;
  };

  const LandingPageComponent = (
    <>
      <header
        className={classnames(
          s.landingPageContainer,
          s.itemsCenter,
          textColorLandingpage ? selectBgBlueLandingPage?.[textColorLandingpage] : s.blur,
        )}>
        <nav
          className={classnames(
            s.nav_con,
            s.itemsCenter,
            s.left,
            headerColorModes[headerColorMode || 'dark'],
          )}>
          <BaseLink
            isNavLink={true}
            to={routesConfig.home()}
            className={classnames(s.logo, s.itemsCenter)}
            activeStyle={false}>
            {selectLogoLandingPage()}
          </BaseLink>

          <div className={s.landingPageMenu}>{LandingPageMenuItem()}</div>
        </nav>

        <div className={s.rightItems}>
          <div className={s.burguerButton}>
            <BurguerButton open={openButtonMenu} onClick={setOpenButtonMenu} />
          </div>
          <nav
            className={classnames(
              s.itemsCenter,
              s.nav_con,
              s.right,
              headerColorModes[headerColorMode || 'dark'],
            )}>
            {isLandingPage && <div className={s.boxIcons}>{IconsItems()}</div>}

            <Link to={routesConfig.singularityMap()} className={classnames(s.startJourney)}>
              {appMode === EAppMode.Mobile ? 'Start now' : 'Start your journey'}
            </Link>
          </nav>
        </div>
      </header>
      <div className={`${s.responsiveMenu} ${openButtonMenu ? s.open : {}}`}>
        <div className={s.landingPageMenuResponsive}>{LandingPageMenuItem()}</div>
        <div className={s.socialIconsResponsive}>{IconsItems()}</div>
      </div>
    </>
  );

  if (isLandingPage) {
    return LandingPageComponent;
  }

  return isLoggedIn ? LoggedComponent : NotLoggedComponent;
};
